import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Check, ArrowRight, Calendar } from 'lucide-react';
import SEO from '@/components/SEO';
import { track } from '@/lib/track';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || '';

export default function BookViewingPage() {
  const [searchParams] = useSearchParams();
  const prefilledLocation = searchParams.get('location') || '';
  const [locations, setLocations] = useState([]);
  const [showFallback, setShowFallback] = useState(!CALENDLY_URL);

  useEffect(() => {
    axios.get(`${API}/public/locations`).then(r => setLocations(r.data)).catch(() => {});
  }, []);

  // Load Calendly widget script
  useEffect(() => {
    if (!CALENDLY_URL || showFallback) return;
    if (!document.querySelector('script[src*="calendly"]')) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, [showFallback]);

  return (
    <div data-testid="book-viewing-page">
      <SEO title="Book a Viewing" description="Book a studio viewing. Tell us what you need and we'll show you a suitable space." path="/book-a-viewing" />

      <section className="py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1A1A] tracking-tight mb-4">
              Book a viewing
            </h1>
            <p className="text-base text-[#5C5C55] max-w-md mx-auto">
              Tell us what you're looking for and we'll arrange a time to show you a suitable studio. We'll guide you through the process and help you find the right space.
            </p>
          </div>

          {/* Calendly embed */}
          {CALENDLY_URL && !showFallback && (
            <div data-testid="calendly-embed">
              <div className="calendly-inline-widget bg-white rounded-2xl border border-[#E5E2DC] overflow-hidden"
                data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&hide_landing_page_details=1`}
                style={{ minWidth: '320px', height: '700px' }} />
              <div className="text-center mt-4">
                <button onClick={() => setShowFallback(true)} className="text-xs text-[#8A8B85] hover:text-[#5C5C55] underline" data-testid="show-fallback-form">
                  Prefer to fill in a form instead?
                </button>
              </div>
            </div>
          )}

          {/* Fallback enquiry form */}
          {showFallback && (
            <div data-testid="fallback-form">
              {CALENDLY_URL && (
                <div className="text-center mb-6">
                  <button onClick={() => setShowFallback(false)} className="text-xs text-[#3A4035] font-medium flex items-center gap-1.5 mx-auto hover:underline" data-testid="show-calendly">
                    <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />Schedule directly with Calendly
                  </button>
                </div>
              )}
              <FallbackForm prefilledLocation={prefilledLocation} locations={locations} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function FallbackForm({ prefilledLocation, locations }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', business_type: '', preferred_location: prefilledLocation, message: '' });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setError(''); };

  useEffect(() => { if (prefilledLocation) set('preferred_location', prefilledLocation); }, [prefilledLocation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setError('Name and email are required'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/public/enquiry`, { ...form, source: 'website' });
      track('book_viewing_submitted', { location: form.preferred_location });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setSaving(false); }
  };

  if (done) {
    return (
      <div className="py-10" data-testid="booking-success">
        <div className="max-w-md mx-auto text-center animate-fade-in">
          <div className="w-14 h-14 rounded-2xl bg-[#E8EDE5] flex items-center justify-center mx-auto mb-5">
            <Check className="w-7 h-7 text-[#3A4035]" strokeWidth={1.5} />
          </div>
          <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-3">Thanks for your enquiry</h2>
          <p className="text-sm text-[#5C5C55] leading-relaxed">We've received your enquiry and will be in touch shortly to arrange a viewing.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      {error && <div className="mb-6 p-4 bg-[#FDF2F2] text-[#B33A3A] text-sm rounded-xl" data-testid="form-error">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Full Name <span className="text-[#B33A3A]">*</span></Label>
          <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Your full name" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="enquiry-name" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Email <span className="text-[#B33A3A]">*</span></Label>
            <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@example.com" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="enquiry-email" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Phone</Label>
            <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="07700 000000" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="enquiry-phone" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Service Type</Label>
            <Input value={form.business_type} onChange={e => set('business_type', e.target.value)} placeholder="e.g. Beauty, Massage" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="enquiry-service" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Preferred Location</Label>
            <Select value={form.preferred_location} onValueChange={v => set('preferred_location', v)}>
              <SelectTrigger className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11" data-testid="enquiry-location"><SelectValue placeholder="Any location" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any location</SelectItem>
                {locations.map(l => <SelectItem key={l.site_id} value={l.name}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Message (optional)</Label>
          <Textarea value={form.message} onChange={e => set('message', e.target.value)} placeholder="Tell us about your requirements..." rows={3} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl resize-none placeholder:text-[#8A8B85]" data-testid="enquiry-message" />
        </div>
        <p className="text-sm text-[#5C5C55]/80">View a studio. See how it works. Decide with confidence.</p>
        <Button type="submit" disabled={saving || !form.name.trim() || !form.email.trim()} className="w-full bg-[#3A4035] text-white rounded-full h-12 text-sm font-medium hover:bg-[#2B3027]" data-testid="enquiry-submit">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending...</> : <>Send Enquiry <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} /></>}
        </Button>
        <p className="text-sm text-[#8A8B85] text-center">No pressure. No obligation. Just come and see.</p>
        <p className="text-sm text-[#5C5C55]/70 text-center">Studios are limited — each space is intentionally one of one.</p>
      </form>
    </div>
  );
}
